import { Paper } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid";
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, addMoney } from "../../util/quantity";
import { toast } from "react-toastify";
import { BatchTask } from "../../util/Batch";
import { Util } from "../../util/Util"
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { Item } from "./InventoryContext";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { applyQuantityStepperColumns, quantityStepperGridSx } from "../../components/quantityStepperColumns";
import { CatalogItemNameCell } from "../../components/CatalogItemActions";
import { useBufferedTaskItems } from "../../hooks/useBufferedTaskItems";
import { useActiveTaskStatusTotal } from "../../hooks/useActiveTaskStatusTotal";
import { ItemTableGridApi } from "../../util/itemTableFlush";

interface Props<T extends Item, U extends BatchTask<T>> {
	bundle?: any;
	activeTask: U | undefined;
	setPendingTasks: Dispatch<SetStateAction<U[]>>;
	columns: GridColDef<T>[];
	itemConstructor: new (data: Partial<T>) => T; // Constructor for T
	items: any[]; // List of all items with stock information
	quantityStepper?: boolean;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
}

const ItemTable = <T extends Item, U extends BatchTask<T>>(
	{ activeTask, setPendingTasks, columns, itemConstructor, quantityStepper = true, apiRef }: Props<T, U>
) => {
	const { t } = useTranslation();

	const bufferedApiRef = apiRef as { current: ItemTableGridApi<T> | null };
	const { gridRows, gridRowsRef, replaceGridRows } = useBufferedTaskItems<T>({
		activeTask,
		setPendingTasks,
		apiRef: bufferedApiRef,
	});

	const handleProcessRowUpdate = useCallback((newRow: Partial<T>, oldRow: T): T => {
		if (newRow.quantity !== oldRow.quantity) {
			const newQuantity = newRow.quantity;

			const fractional = Boolean((oldRow as { isFractional?: boolean }).isFractional);
			if (newQuantity === undefined || newQuantity < 0 || newQuantity > QTY_LIMITS.max) {
				toast.error(t("common:validation.countRange", { max: Util.formatNumber(QTY_LIMITS.max) }));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId, stockDifference: oldRow.stockDifference, oldValue: oldRow.oldValue, valueDifference: oldRow.valueDifference } as T;
				return oldRow;
			}
			if (!fractional && !isWholeNumber(newQuantity)) {
				toast.error(t("common:validation.quantityMustBeWhole"));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId, stockDifference: oldRow.stockDifference, oldValue: oldRow.oldValue, valueDifference: oldRow.valueDifference } as T; // prevent total and uniqueId from being lost and having #undefined ID
				return oldRow;
			}
		}

		if (newRow.price !== oldRow.price) {
			const newAmount = newRow.price;

			if (newAmount === undefined || newAmount < 0 || newAmount > MONEY_LIMITS.max) {
				toast.error(t("common:validation.amountRange", { max: Util.formatNumber(MONEY_LIMITS.max) }));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId, stockDifference: oldRow.stockDifference, oldValue: oldRow.oldValue, valueDifference: oldRow.valueDifference } as T; // prevent total and uniqueId from being lost and having #undefined ID
				return oldRow;
			}
		}

		const updatedRow = new itemConstructor({ ...oldRow, ...newRow });

		const nextRows = gridRowsRef.current.map((item) =>
			item.uniqueId === updatedRow.uniqueId ? updatedRow : item
		);

		replaceGridRows(nextRows);

		return { ...updatedRow, total: updatedRow.total, uniqueId: updatedRow.uniqueId, stockDifference: updatedRow.stockDifference, oldValue: updatedRow.oldValue, valueDifference: updatedRow.valueDifference } as T; // Ensure total and uniqueId are included
	}, [gridRowsRef, itemConstructor, replaceGridRows, t]);

	const onQuantityChangeByRowId = useCallback((rowId: string, newValue: number) => {
		const row = gridRowsRef.current.find((item) => item.uniqueId === rowId);
		if (!row) return;
		handleProcessRowUpdate({ ...row, quantity: newValue }, row);
	}, [gridRowsRef, handleProcessRowUpdate]);

	const quantityMaxByRowId = useMemo(() => {
		const map = new Map<string, number>();
		for (const row of gridRows) {
			map.set(row.uniqueId, QTY_LIMITS.max);
		}
		return map;
	}, [gridRows]);

	const columnsWithHoverActions = useMemo(() => {
		return columns.map((col) => {
			if (col.field !== "item") return col;
			const originalRenderCell = col.renderCell;
			return {
				...col,
				renderCell: (params: GridRenderCellParams<T>) => {
					const cellContent = originalRenderCell ? originalRenderCell(params) : (params.value ?? "");
					const itemId = params.row.id;
					const itemName = params.row.item || String(params.value || "");
					return (
						<CatalogItemNameCell itemId={itemId} itemName={itemName} isPos={false}>
							{cellContent}
						</CatalogItemNameCell>
					);
				},
			};
		});
	}, [columns]);

	const columnsToRender = useMemo(
		() => quantityStepper
			? applyQuantityStepperColumns(columnsWithHoverActions, {
				quantityMin: 0,
				quantityMaxByRowId,
				onQuantityChangeByRowId,
				getIsFractional: (row) => Boolean((row as { isFractional?: boolean }).isFractional),
			})
			: columnsWithHoverActions,
		[columnsWithHoverActions, onQuantityChangeByRowId, quantityMaxByRowId, quantityStepper],
	);

	const paginationModel = useMemo(() => ({ page: 0, pageSize: 100 }), []);

	const gridTotal = useMemo(
		() => gridRows.reduce((sum, item) => addMoney(sum, item.total), 0),
		[gridRows],
	);

	useActiveTaskStatusTotal(gridTotal, gridRows.length, Boolean(activeTask));

	return (
		<Paper elevation={24} sx={{ width: '100%' }}>
			<DataGrid<T>
				apiRef={apiRef}
				rows={gridRows}
				columns={columnsToRender}
				density="compact"
				getRowId={(row) => row.uniqueId}
				initialState={{ pagination: { paginationModel } }}
				pageSizeOptions={[100, 150, 200, 500, 1000, 2000, 3000, 4000, 5000, 10000]}
				processRowUpdate={handleProcessRowUpdate}
				checkboxSelection
				disableRowSelectionOnClick
				sx={{
					'& .MuiDataGrid-columnHeaders': {
						fontWeight: 'bolder',
						fontSize: 16,
					},
					'& .MuiDataGrid-columnHeaderTitle': {
						fontWeight: 900 + '!important',
					},
					'& .MuiDataGrid-row:hover .catalog-item-option-actions': {
						opacity: 1,
						pointerEvents: "auto",
					},
					...quantityStepperGridSx,
				}}
			/>
		</Paper>
	);
};

export default ItemTable;